import { useEffect, useRef } from "react";
import { useChatStore } from "../../state/chat";
import { ActionCard } from "./ActionCard";
import { ConfirmationCard } from "./ConfirmationCard";
import { Receipt } from "./Receipt";
import { RipperMessage } from "./RipperMessage";
import { SuggestionChips } from "./SuggestionChips";
import { UserMessage } from "./UserMessage";
import styles from "./ChatStream.module.css";

interface ChatStreamProps {
  onSend?: (text: string) => void;
}

export function ChatStream({ onSend }: ChatStreamProps) {
  const entries = useChatStore((s) => s.entries);
  const mode = useChatStore((s) => s.mode);
  const resolveConfirmation = useChatStore((s) => s.resolveConfirmation);
  const completeAction = useChatStore((s) => s.completeAction);
  const overrideAction = useChatStore((s) => s.overrideAction);
  const scrollerRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    const el = scrollerRef.current;
    if (!el) return;
    el.scrollTop = el.scrollHeight;
  }, [entries]);

  return (
    <div className={styles.scroller} ref={scrollerRef} role="log" aria-live="polite">
      <div className={styles.stack}>
        {entries.map((entry, i) => {
          switch (entry.kind) {
            case "ripper": {
              const prev = entries[i - 1];
              const showLabel = !prev || prev.kind !== "ripper";
              return (
                <RipperMessage
                  key={entry.id}
                  text={entry.text}
                  streaming={entry.streaming}
                  showLabel={showLabel}
                />
              );
            }
            case "user":
              return <UserMessage key={entry.id} text={entry.text} />;
            case "confirmation":
              return (
                <ConfirmationCard
                  key={entry.id}
                  title={entry.title}
                  body={entry.body}
                  actions={entry.actions}
                  resolved={entry.resolvedWith}
                  onResolve={(action) => resolveConfirmation(entry.id, action)}
                />
              );
            case "action":
              return (
                <ActionCard
                  key={entry.id}
                  id={entry.id}
                  title={entry.title}
                  body={entry.body}
                  state={entry.state}
                  actMs={entry.actMs}
                  onComplete={completeAction}
                  onOverride={overrideAction}
                />
              );
            case "receipt":
              return <Receipt key={entry.id} text={entry.text} />;
            case "suggestions":
              return (
                <SuggestionChips
                  key={entry.id}
                  title={entry.title}
                  prompts={entry.prompts}
                  onPick={(prompt) => onSend?.(prompt)}
                  disabled={mode !== "listening"}
                />
              );
          }
        })}
      </div>
    </div>
  );
}
